import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { App, Button, Drawer, Form, Input, InputNumber, Select, Space, Table, Tag, Typography, } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiError } from '@/lib/api';
import { useAuthStore } from '@/lib/auth-store';
import { formatRupiah } from '@/lib/format';
const STATUS_COLORS = {
    AVAILABLE: 'green',
    RESERVED: 'gold',
    SOLD: 'volcano',
    RENTED: 'orange',
    OCCUPIED: 'purple',
};
export function PlotsPage() {
    const { message } = App.useApp();
    const qc = useQueryClient();
    const hasPerm = useAuthStore((s) => s.hasPerm);
    const [blockFilter, setBlockFilter] = useState();
    const [statusFilter, setStatusFilter] = useState();
    const [drawer, setDrawer] = useState({ open: false });
    const [form] = Form.useForm();
    const blocks = useQuery({
        queryKey: ['makam-blocks-select'],
        queryFn: async () => (await api.get('/makam/blocks')).data,
    });
    const plots = useQuery({
        queryKey: ['makam-plots', blockFilter, statusFilter],
        queryFn: async () => (await api.get('/makam/plots', {
            params: { block_id: blockFilter, status: statusFilter },
        })).data,
    });
    const createMut = useMutation({
        mutationFn: async (v) => (await api.post('/makam/plots', v)).data,
        onSuccess: () => {
            message.success('Petak ditambahkan');
            qc.invalidateQueries({ queryKey: ['makam-plots'] });
            qc.invalidateQueries({ queryKey: ['makam-blocks'] });
            setDrawer({ open: false });
        },
        onError: (e) => message.error(apiError(e)),
    });
    const patchMut = useMutation({
        mutationFn: async ({ id, body }) => (await api.patch(`/makam/plots/${id}`, body)).data,
        onSuccess: () => {
            message.success('Petak diperbarui');
            qc.invalidateQueries({ queryKey: ['makam-plots'] });
            setDrawer({ open: false });
        },
        onError: (e) => message.error(apiError(e)),
    });
    const summary = useMemo(() => {
        const s = { AVAILABLE: 0, RESERVED: 0, SOLD: 0, RENTED: 0, OCCUPIED: 0 };
        plots.data?.forEach((p) => { s[p.status] = (s[p.status] ?? 0) + 1; });
        return s;
    }, [plots.data]);
    const cols = [
        { title: 'Blok', dataIndex: 'block_code', width: 80 },
        { title: 'Kode Petak', dataIndex: 'plot_code', width: 140 },
        { title: 'Luas (m²)', dataIndex: 'size_m2', width: 110, align: 'right' },
        {
            title: 'Status',
            dataIndex: 'status',
            width: 130,
            render: (s) => _jsx(Tag, { color: STATUS_COLORS[s], children: s }),
        },
        {
            title: 'Harga Jual',
            dataIndex: 'price_sale',
            width: 160,
            align: 'right',
            render: (v) => v ? _jsx("span", { className: "money", children: formatRupiah(v) }) : '—',
        },
        {
            title: 'Sewa/Tahun',
            dataIndex: 'price_rent_year',
            width: 160,
            align: 'right',
            render: (v) => v ? _jsx("span", { className: "money", children: formatRupiah(v) }) : '—',
        },
        { title: 'Catatan', dataIndex: 'notes' },
        {
            title: 'Aksi',
            width: 90,
            render: (_, r) => hasPerm('makam.plot:edit') && (_jsx(Button, { size: "small", icon: _jsx(EditOutlined, {}), onClick: () => { setDrawer({ open: true, row: r }); form.setFieldsValue(r); } })),
        },
    ];
    return (_jsxs("div", { children: [_jsxs(Space, { style: { marginBottom: 16, display: 'flex', justifyContent: 'space-between', width: '100%' }, children: [_jsx(Typography.Title, { level: 3, style: { margin: 0 }, children: "Petak Makam" }), hasPerm('makam.plot:create') && (_jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), onClick: () => { form.resetFields(); setDrawer({ open: true }); }, children: "Tambah Petak" }))] }), _jsxs(Space, { style: { marginBottom: 16 }, wrap: true, children: [_jsx(Select, { allowClear: true, style: { width: 180 }, placeholder: "Filter blok", value: blockFilter, onChange: (v) => setBlockFilter(v), options: (blocks.data ?? []).map((b) => ({ value: b.id, label: `${b.code} — ${b.name}` })) }), _jsx(Select, { allowClear: true, style: { width: 180 }, placeholder: "Filter status", value: statusFilter, onChange: (v) => setStatusFilter(v), options: Object.keys(STATUS_COLORS).map((s) => ({ value: s, label: s })) }), Object.entries(summary).map(([k, v]) => (_jsxs(Tag, { color: STATUS_COLORS[k], children: [k, ": ", v] }, k)))] }), _jsx(Table, { rowKey: "id", loading: plots.isLoading, dataSource: plots.data ?? [], columns: cols, size: "middle", pagination: { pageSize: 30 } }), _jsx(Drawer, { open: drawer.open, onClose: () => setDrawer({ open: false }), title: drawer.row ? `Edit ${drawer.row.plot_code}` : 'Tambah Petak', width: 480, destroyOnClose: true, extra: _jsx(Button, { type: "primary", loading: createMut.isPending || patchMut.isPending, onClick: () => form.validateFields().then((v) => {
                        const payload = {
                            ...v,
                            size_m2: v.size_m2?.toString(),
                            price_sale: v.price_sale?.toString(),
                            price_rent_year: v.price_rent_year?.toString(),
                        };
                        if (drawer.row)
                            patchMut.mutate({ id: drawer.row.id, body: payload });
                        else
                            createMut.mutate(payload);
                    }), children: "Simpan" }), children: _jsxs(Form, { form: form, layout: "vertical", preserve: false, children: [_jsx(Form.Item, { name: "block_id", label: "Blok", rules: [{ required: !drawer.row }], children: _jsx(Select, { disabled: !!drawer.row, options: (blocks.data ?? []).map((b) => ({ value: b.id, label: `${b.code} — ${b.name}` })) }) }), _jsx(Form.Item, { name: "plot_code", label: "Kode Petak", rules: [{ required: true }], children: _jsx(Input, { placeholder: "mis. A-01-15", disabled: !!drawer.row }) }), _jsx(Form.Item, { name: "size_m2", label: "Luas (m\u00B2)", children: _jsx(InputNumber, { style: { width: '100%' }, min: 0, step: 0.1 }) }), _jsx(Form.Item, { name: "price_sale", label: "Harga Jual (Rp)", children: _jsx(InputNumber, { style: { width: '100%' }, min: 0, formatter: (v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.'), parser: ((v) => parseFloat((v ?? '').replace(/\./g, '')) || 0) }) }), _jsx(Form.Item, { name: "price_rent_year", label: "Sewa per Tahun (Rp)", children: _jsx(InputNumber, { style: { width: '100%' }, min: 0, formatter: (v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.'), parser: ((v) => parseFloat((v ?? '').replace(/\./g, '')) || 0) }) }), drawer.row && (_jsx(Form.Item, { name: "status", label: "Status", children: _jsx(Select, { options: Object.keys(STATUS_COLORS).map((s) => ({ value: s, label: s })) }) })), _jsx(Form.Item, { name: "notes", label: "Catatan", children: _jsx(Input.TextArea, { rows: 2 }) })] }) })] }));
}
